import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Download, 
  Calendar,
  TrendingUp,
  Users,
  Building2,
  Package,
  CreditCard,
  BarChart3,
  PieChart,
  LineChart,
  Filter
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Report {
  id: string;
  name: string;
  type: 'hub' | 'user' | 'revenue' | 'activity' | 'security';
  format: 'pdf' | 'csv' | 'excel';
  lastGenerated: string;
  status: 'available' | 'generating' | 'failed';
  size: string;
  description: string;
}

const mockReports: Report[] = [
  {
    id: '1',
    name: 'Hub Performance Report',
    type: 'hub',
    format: 'pdf',
    lastGenerated: '2024-08-13T10:00:00Z',
    status: 'available',
    size: '2.3 MB',
    description: 'Comprehensive analysis of all hub performance metrics'
  },
  {
    id: '2',
    name: 'User Activity Summary',
    type: 'user',
    format: 'csv',
    lastGenerated: '2024-08-13T09:30:00Z',
    status: 'available',
    size: '1.8 MB',
    description: 'Detailed user activity and engagement data'
  },
  {
    id: '3',
    name: 'Revenue Analysis',
    type: 'revenue',
    format: 'excel',
    lastGenerated: '2024-08-12T16:00:00Z',
    status: 'available',
    size: '3.1 MB',
    description: 'Monthly revenue breakdown and projections'
  },
  {
    id: '4',
    name: 'Security Audit Report',
    type: 'security',
    format: 'pdf',
    lastGenerated: '2024-08-12T14:00:00Z',
    status: 'available',
    size: '1.5 MB',
    description: 'Security events and compliance status'
  },
  {
    id: '5',
    name: 'Package Usage Statistics',
    type: 'activity',
    format: 'csv',
    lastGenerated: '2024-08-11T12:00:00Z',
    status: 'available',
    size: '2.7 MB',
    description: 'Package creation and usage patterns'
  }
];

const AdminReports: React.FC = () => {
  const [reports, setReports] = useState<Report[]>(mockReports);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [formatFilter, setFormatFilter] = useState<string>('all');

  const filteredReports = reports.filter(report => {
    const matchesType = typeFilter === 'all' || report.type === typeFilter;
    const matchesFormat = formatFilter === 'all' || report.format === formatFilter;
    return matchesType && matchesFormat;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'hub':
        return <Building2 className="w-4 h-4" />;
      case 'user':
        return <Users className="w-4 h-4" />;
      case 'revenue':
        return <CreditCard className="w-4 h-4" />;
      case 'activity':
        return <TrendingUp className="w-4 h-4" />;
      case 'security':
        return <FileText className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getFormatBadge = (format: string) => {
    switch (format) {
      case 'pdf':
        return <Badge className="bg-red-100 text-red-800">PDF</Badge>;
      case 'csv':
        return <Badge className="bg-green-100 text-green-800">CSV</Badge>;
      case 'excel':
        return <Badge className="bg-blue-100 text-blue-800">Excel</Badge>;
      default:
        return <Badge variant="secondary">{format.toUpperCase()}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge className="bg-green-100 text-green-800">Available</Badge>;
      case 'generating':
        return <Badge className="bg-yellow-100 text-yellow-800">Generating</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const generateReport = (reportId: string) => {
    // Mock report generation
    setReports(prev => prev.map(report => 
      report.id === reportId 
        ? { ...report, status: 'generating' as const }
        : report
    ));
    
    // Simulate generation delay
    setTimeout(() => {
      setReports(prev => prev.map(report => 
        report.id === reportId 
          ? { ...report, status: 'available' as const, lastGenerated: new Date().toISOString() }
          : report
      ));
    }, 3000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-600">Generate and manage system reports</p>
        </div>
        <Button>
          <FileText className="w-4 h-4 mr-2" />
          Create Custom Report
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <FileText className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Reports</p>
                <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Download className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-gray-900">
                  {reports.filter(r => r.status === 'available').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">12</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Last Generated</p>
                <p className="text-sm font-bold text-gray-900">2 hours ago</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="hub">Hub Reports</SelectItem>
                <SelectItem value="user">User Reports</SelectItem>
                <SelectItem value="revenue">Revenue Reports</SelectItem>
                <SelectItem value="activity">Activity Reports</SelectItem>
                <SelectItem value="security">Security Reports</SelectItem>
              </SelectContent>
            </Select>
            <Select value={formatFilter} onValueChange={setFormatFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Formats</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="excel">Excel</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.map((report) => (
          <Card key={report.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {getTypeIcon(report.type)}
                  <CardTitle className="text-lg">{report.name}</CardTitle>
                </div>
                {getFormatBadge(report.format)}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">{report.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Status:</span>
                  {getStatusBadge(report.status)}
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Size:</span>
                  <span className="font-medium">{report.size}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Last Generated:</span>
                  <span className="font-medium">{formatDate(report.lastGenerated)}</span>
                </div>
              </div>

              <div className="flex space-x-2">
                {report.status === 'available' ? (
                  <Button size="sm" className="flex-1">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                ) : (
                  <Button size="sm" className="flex-1" disabled>
                    <Download className="w-4 h-4 mr-2" />
                    {report.status === 'generating' ? 'Generating...' : 'Failed'}
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => generateReport(report.id)}
                  disabled={report.status === 'generating'}
                >
                  <FileText className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Report Generation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button variant="outline" className="h-20 flex-col">
              <Users className="w-6 h-6 mb-2" />
              <span className="text-sm">User Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex-col">
              <Building2 className="w-6 h-6 mb-2" />
              <span className="text-sm">Hub Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex-col">
              <CreditCard className="w-6 h-6 mb-2" />
              <span className="text-sm">Revenue Report</span>
            </Button>
            <Button variant="outline" className="h-20 flex-col">
              <TrendingUp className="w-6 h-6 mb-2" />
              <span className="text-sm">Activity Report</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReports;
